/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { type ISupportedLocale } from 'uplisting-frontend/services/locale';

interface ILocaleOption extends ISupportedLocale {
  active: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface IArgs {}

interface PopoverSettingsMenuSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiPopoverSettingsMenu extends Component<PopoverSettingsMenuSignature> {
  @service session!: Services['session'];
  @service router!: Services['router'];
  @service locale!: Services['locale'];
  @service features!: Services['features'];
  @service notifications!: Services['notifications'];

  @tracked languageMenuOpen = false;

  @cached
  get currentUser(): ModelRegistry['user'] {
    return this.session.currentUser as ModelRegistry['user'];
  }

  get isLocalePickerEnabled(): boolean {
    return this.features.isLocalePickerEnabled;
  }

  get isTeamPresetsEnabled(): boolean {
    return this.features.isTeamPresetsEnabled;
  }

  get localeOptions(): ILocaleOption[] {
    const { activeLocale } = this.locale;

    return this.locale.supportedLocales.map((option) => ({
      ...option,
      active: option.code === activeLocale,
    }));
  }

  @action
  handleLogout(): void {
    this.session.handleLogout();
  }

  @action
  toggleLanguageMenu(event: MouseEvent): void {
    // Keep the settings popover open (the sidebar closes it on any document
    // click) so the accordion can expand in place.
    event.stopPropagation();

    this.languageMenuOpen = !this.languageMenuOpen;
  }

  @action
  async handleSelectLocale(code: string, event: MouseEvent): Promise<void> {
    // Keep the popover open so the UI visibly re-renders in the chosen language.
    event.stopPropagation();

    try {
      await this.locale.setLocale(code);
    } catch {
      // A failed switch (e.g. the locale's translations 404) shouldn't be a
      // silent no-op / unhandled rejection — surface it.
      this.notifications.error();
    }
  }

  @action
  handleConnectAirbnb(): void {
    if ((this.router.currentRouteName ?? '').startsWith('calendar.starts')) {
      this.router.refresh();
    } else {
      this.router.transitionTo('calendar.starts', 'me');
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Popovers::SettingsMenu': typeof UiPopoverSettingsMenu;
  }
}
